import { useEffect, useMemo, useState } from 'react';

export default function CommandesTab({
    tables,
    menuItems,
    ingredients,
    onUpdateTableOrder,
    onSendOrder,
    onMarkOrderAsServed,
    onPayOrder,
    getPortionsRemaining,
    onOpenPrepareModal,
}) {
    const [selectedTableId, setSelectedTableId] = useState(tables[0]?.id ?? null);
    const [selectedCategoryId, setSelectedCategoryId] = useState(null);
    const [dishSearch, setDishSearch] = useState('');

    // États liés au paiement (déplacés en haut : ils sont utilisés par buildReceiptHtml)
    const [paymentMethod, setPaymentMethod] = useState('cash');
    const [paymentReference, setPaymentReference] = useState('');
    const [amountReceived, setAmountReceived] = useState('');
    const [paymentError, setPaymentError] = useState('');

    useEffect(() => {
        if (!selectedTableId && tables.length) {
            setSelectedTableId(tables[0].id);
        }
    }, [selectedTableId, tables]);

    const selectedTable = useMemo(() => {
        return (
            tables.find((table) => table.id === selectedTableId) ||
            tables[0] ||
            { id: null, name: 'Aucune table', status: 'free', order: null }
        );
    }, [tables, selectedTableId]);

    const isOrderEditable = !selectedTable.order?.status || selectedTable.order?.status === 'draft';

    // Commande "brute" telle qu'enregistrée sur la table (utile pour l'historique/reçu)
    const rawOrder = selectedTable.order ?? null;

    // Commande affichée/éditée : si la précédente commande est terminée, on repart
    // sur une commande vide, mais on garde les dates de l'ancienne commande pour l'affichage.
    const order = rawOrder?.status === 'completed'
        ? { items: [], notes: '', status: 'draft' }
        : rawOrder ?? { items: [], notes: '', status: 'draft' };

    const orderItems = order.items.map((line) => {
        const menuItem = menuItems.find((item) => item.id === line.menuItemId);
        return {
            ...line,
            menuItem,
            name: line.name || menuItem?.name || 'Plat',
            category: menuItem?.category || line.category || null,
        };
    });

    const orderTotal = orderItems.reduce((sum, item) => sum + (item.menuItem?.price ?? 0) * item.quantity, 0);

    // Corrigé : une commande "completed" ne doit pas rouvrir l'encaissement
    // (les champs de l'ancienne commande ont déjà été réinitialisés ci-dessus).
    const isOrderPayable = !!rawOrder?.id && rawOrder?.status !== 'draft' && rawOrder?.status !== 'completed';

    const buildReceiptHtml = () => {
        const createdAt = selectedTable.order?.createdAt || new Date().toISOString();
        const formattedDate = new Date(createdAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
        const itemsHtml = orderItems.length > 0
            ? orderItems.map((item) => {
                  const name = item.menuItem?.name || item.menuItemName || item.name || 'Article';
                  const qty = item.quantity;
                  const unitPrice = Number(item.menuItem?.price ?? item.price ?? 0).toFixed(2);
                  const total = (qty * Number(item.menuItem?.price ?? item.price ?? 0)).toFixed(2);
                  return `<tr><td>${name}</td><td>${qty}</td><td>${unitPrice}</td><td>${total}</td></tr>`;
              }).join('')
            : `<tr><td colspan="4">Aucun article</td></tr>`;

        return `<!DOCTYPE html>
<html lang="fr">
<head>
<meta charset="UTF-8">
<title>Ticket</title>
<style>
body { width: 80mm; font-family: Arial, sans-serif; margin: 0; padding: 10px; }
.ticket { width: 100%; }
.ticket h1, .ticket h2, .ticket h3 { margin: 0; text-align: center; }
.ticket table { width: 100%; border-collapse: collapse; margin-top: 10px; }
.ticket th, .ticket td { padding: 4px 0; font-size: 12px; }
.ticket th { text-align: left; }
.ticket .total { font-size: 14px; font-weight: bold; }
.ticket .footer { margin-top: 12px; text-align: center; font-size: 12px; }
@media print {
  body { margin: 0; padding: 0; }
  .no-print { display: none !important; }
}
</style>
</head>
<body>
<div class="ticket">
<h1>Plan B</h1>
<h2>Ticket de caisse</h2>
<p><strong>Table :</strong> ${selectedTable.name}</p>
<p><strong>Date :</strong> ${formattedDate}</p>
<hr>
<table>
<thead>
<tr><th>Produit</th><th>Qté</th><th>PU</th><th>Total</th></tr>
</thead>
<tbody>
${itemsHtml}
</tbody>
</table>
<hr>
<p class="total">Total : Ar ${orderTotal.toFixed(2)}</p>
<p><strong>Moyen de paiement :</strong> ${paymentMethod}</p>
${paymentMethod === 'cash' ? `<p><strong>Montant reçu :</strong> Ar ${Number(amountReceived || orderTotal).toFixed(2)}</p><p><strong>Monnaie :</strong> Ar ${cashChange.toFixed(2)}</p>` : ''}
<div class="footer">
<p>Merci de votre visite !</p>
</div>
</div>
<script>
window.onload = function() {
  window.print();
};
</script>
</body>
</html>`;
    };

    const printBrowserReceipt = () => {
        const receiptWindow = window.open('', '_blank');
        if (!receiptWindow) return;
        receiptWindow.document.write(buildReceiptHtml());
        receiptWindow.document.close();
    };

    const statusLabels = {
        available: 'Libre',
        occupied: 'Occupée',
        reserved: 'Réservée',
        cleaning: 'Nettoyage',
        inactive: 'Inactive',
    };

    const statusClasses = {
        available: 'bg-green-100 text-green-700',
        occupied: 'bg-red-100 text-red-600',
        reserved: 'bg-orange-100 text-orange-700',
        cleaning: 'bg-yellow-100 text-yellow-700',
        inactive: 'bg-slate-100 text-slate-600',
    };

    const paymentDue = orderTotal;
    const cashChange = paymentMethod === 'cash'
        ? Math.max(0, (Number(amountReceived) || 0) - paymentDue)
        : 0;

    const menuItemsByCategory = useMemo(() => {
        const grouped = {};
        menuItems.forEach((item) => {
            const categoryId = item.dish_category_id || 'sans-categorie';
            const categoryName = item.category || (item.dish_category_id ? `Catégorie ${item.dish_category_id}` : 'Sans catégorie');
            if (!grouped[categoryId]) {
                grouped[categoryId] = { name: categoryName, items: [] };
            }
            grouped[categoryId].items.push(item);
        });
        return grouped;
    }, [menuItems]);

    const categoryOrder = useMemo(() => {
        return Object.keys(menuItemsByCategory).sort((a, b) => {
            const aName = menuItemsByCategory[a].name;
            const bName = menuItemsByCategory[b].name;
            return aName.localeCompare(bName, 'fr');
        });
    }, [menuItemsByCategory]);

    // Sélectionne automatiquement la première catégorie disponible
    useEffect(() => {
        if (categoryOrder.length && (!selectedCategoryId || !categoryOrder.includes(selectedCategoryId))) {
            setSelectedCategoryId(categoryOrder[0]);
        }
    }, [categoryOrder, selectedCategoryId]);

    // Quantité déjà dans le panier, par catégorie -> permet d'afficher un badge sur chaque onglet
    const quantityByCategory = useMemo(() => {
        const totals = {};
        orderItems.forEach((item) => {
            const categoryId = item.menuItem?.dish_category_id || 'sans-categorie';
            totals[categoryId] = (totals[categoryId] || 0) + item.quantity;
        });
        return totals;
    }, [orderItems]);

    // Quantité déjà dans le panier pour un plat donné
    const quantityByMenuItem = useMemo(() => {
        const totals = {};
        orderItems.forEach((item) => {
            totals[item.menuItemId] = item.quantity;
        });
        return totals;
    }, [orderItems]);

    const visibleDishes = useMemo(() => {
        const category = menuItemsByCategory[selectedCategoryId];
        const items = category ? category.items : [];
        const query = dishSearch.trim().toLowerCase();
        if (!query) return items;
        return items.filter((item) => item.name.toLowerCase().includes(query));
    }, [menuItemsByCategory, selectedCategoryId, dishSearch]);

    const handleSelectMenuItem = (menuItem) => {
        if (!isOrderEditable) return;
        const existing = order.items.find((line) => line.menuItemId === menuItem.id);
        const nextItems = existing
            ? order.items.map((line) =>
                  line.menuItemId === menuItem.id ? { ...line, quantity: line.quantity + 1 } : line
              )
            : [...order.items, { menuItemId: menuItem.id, quantity: 1 }];

        onUpdateTableOrder(selectedTable.id, {
            ...order,
            status: order.status || 'draft',
            createdAt: order.createdAt || new Date().toISOString(),
            items: nextItems,
        });
    };

    const handleQuantityChange = (menuItemId, value) => {
        if (!isOrderEditable) return;
        const nextItems = order.items.map((line) =>
            line.menuItemId === menuItemId ? { ...line, quantity: Math.max(1, Number(value) || 1) } : line
        );
        onUpdateTableOrder(selectedTable.id, { ...order, items: nextItems });
    };

    // Décrémente d'une unité ; retire le plat si la quantité tombe à 0
    const handleDecrementItem = (menuItemId) => {
        if (!isOrderEditable) return;
        const existing = order.items.find((line) => line.menuItemId === menuItemId);
        if (!existing) return;
        if (existing.quantity <= 1) {
            handleRemoveItem(menuItemId);
            return;
        }
        const nextItems = order.items.map((line) =>
            line.menuItemId === menuItemId ? { ...line, quantity: line.quantity - 1 } : line
        );
        onUpdateTableOrder(selectedTable.id, { ...order, items: nextItems });
    };

    const handleRemoveItem = (menuItemId) => {
        if (!isOrderEditable) return;
        const nextItems = order.items.filter((line) => line.menuItemId !== menuItemId);
        onUpdateTableOrder(selectedTable.id, { ...order, items: nextItems });
    };

    const handleNoteChange = (value) => {
        if (!isOrderEditable) return;
        onUpdateTableOrder(selectedTable.id, { ...order, notes: value });
    };

    const formatDateTime = (value) => {
        if (!value) return '';
        try {
            return new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
        } catch {
            return value;
        }
    };

    return (
        <div className="space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Commandes</p>
                        <h2 className="mt-2 text-3xl font-semibold text-green-900">Prise de commande</h2>
                    </div>
                    <div className="rounded-2xl border border-green-400 bg-white px-4 py-3 text-sm text-green-700">
                        <p className="font-medium text-green-900">{tables.length} table{tables.length > 1 ? 's' : ''}</p>
                        <p className="text-green-600">Commandes et paiements</p>
                    </div>
                </div>
            </section>

            <div className="grid gap-6 xl:grid-cols-[360px_1fr]">
                <aside className="rounded-3xl border border-green-400 bg-white/90 p-4">
                    <h3 className="mb-4 text-lg font-semibold text-green-900">Tables</h3>
                    <div className="space-y-3">
                        {tables.map((table) => (
                            <button
                                key={table.id}
                                className={`w-full rounded-3xl border px-4 py-4 text-left transition ${
                                    selectedTable?.id === table.id
                                        ? 'border-green-500 bg-green-600/10 text-green-900'
                                        : 'border-green-400 bg-green-50 text-green-700 hover:border-slate-600'
                                }`}
                                onClick={() => setSelectedTableId(table.id)}
                            >
                                <div className="flex items-center justify-between">
                                    <span className="font-medium">{table.name}</span>
                                    <span
                                        className={`rounded-full px-3 py-1 text-xs font-semibold uppercase ${statusClasses[table.status] || 'bg-slate-100 text-slate-600'}`}
                                    >
                                        {statusLabels[table.status] || 'Inconnu'}
                                    </span>
                                </div>
                                {table.order && (
                                    <p className="mt-2 text-sm text-green-600">{table.order.items.length} plat(s) en cours</p>
                                )}
                            </button>
                        ))}
                    </div>
                </aside>

                <div className="space-y-6">
                    <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                            <div>
                                <p className="text-xs uppercase tracking-[0.35em] text-green-600">Équipe</p>
                                <h3 className="text-2xl font-semibold text-green-900">{selectedTable?.name}</h3>
                            </div>
                            <div className="rounded-2xl bg-white px-4 py-3 text-sm text-green-700">
                                Statut :{' '}
                                <span className="font-semibold text-green-900">
                                    {statusLabels[selectedTable?.status] || 'Statut inconnu'}
                                </span>
                            </div>
                        </div>

                        <div className="mt-5 grid gap-4 lg:grid-cols-[1.4fr_1fr]">
                            <div className="rounded-3xl border border-green-400 bg-white p-4">
                                <h4 className="mb-3 text-base font-semibold text-green-900">Sélectionner des plats</h4>

                                {/* Étape 1 : choisir la catégorie */}
                                <div className="mb-3 flex flex-wrap gap-2">
                                    {categoryOrder.map((categoryId) => {
                                        const category = menuItemsByCategory[categoryId];
                                        const badgeCount = quantityByCategory[categoryId];
                                        const isActive = selectedCategoryId === categoryId;
                                        return (
                                            <button
                                                key={categoryId}
                                                type="button"
                                                onClick={() => setSelectedCategoryId(categoryId)}
                                                className={`relative rounded-full border px-4 py-2 text-sm font-semibold transition ${
                                                    isActive
                                                        ? 'border-green-600 bg-green-600 text-white shadow'
                                                        : 'border-green-300 bg-green-50 text-green-700 hover:border-green-500 hover:bg-white'
                                                }`}
                                            >
                                                {category.name}
                                                <span className={`ml-2 text-xs ${isActive ? 'text-green-100' : 'text-green-400'}`}>
                                                    ({category.items.length})
                                                </span>
                                                {badgeCount ? (
                                                    <span className="absolute -right-2 -top-2 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1 text-[11px] font-bold text-white">
                                                        {badgeCount}
                                                    </span>
                                                ) : null}
                                            </button>
                                        );
                                    })}
                                </div>

                                {/* Recherche rapide dans la catégorie sélectionnée */}
                                <input
                                    type="text"
                                    value={dishSearch}
                                    onChange={(event) => setDishSearch(event.target.value)}
                                    placeholder="Rechercher un plat dans cette catégorie..."
                                    className="mb-3 w-full rounded-2xl border border-green-300 bg-green-50 px-4 py-2 text-sm text-green-900 focus:border-green-500 focus:outline-none"
                                />

                                {/* Étape 2 : choisir le(s) plat(s) de la catégorie sélectionnée */}
                                <div className="grid gap-2 sm:grid-cols-2">
                                    {visibleDishes.map((menuItem) => {
                                        const qtyInCart = quantityByMenuItem[menuItem.id] || 0;
                                        const portionsLeft = getPortionsRemaining(menuItem);
                                        const isOutOfStock = portionsLeft !== null && portionsLeft <= 0;
                                        return (
                                            <div
                                                key={menuItem.id}
                                                className={`flex flex-col justify-between rounded-3xl border px-4 py-3 transition ${
                                                    qtyInCart
                                                        ? 'border-green-500 bg-green-100'
                                                        : 'border-green-400 bg-green-50'
                                                } ${!isOrderEditable || isOutOfStock ? 'opacity-60' : ''}`}
                                            >
                                                <div className="flex items-start justify-between gap-2">
                                                    <div>
                                                        <p className="font-semibold text-green-900">{menuItem.name}</p>
                                                        <p className="text-sm text-green-600">Ar {menuItem.price.toFixed(2)}</p>
                                                    </div>
                                                    <span className="whitespace-nowrap rounded-full bg-green-200 px-3 py-1 text-xs text-green-700">
                                                        {portionsLeft === null ? 'Fournisseur' : `${portionsLeft} portions`}
                                                    </span>
                                                </div>

                                                <div className="mt-3 flex items-center justify-end gap-2">
                                                    {qtyInCart > 0 ? (
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                disabled={!isOrderEditable}
                                                                onClick={() => handleDecrementItem(menuItem.id)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-full border border-green-400 bg-white text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                −
                                                            </button>
                                                            <span className="min-w-[20px] text-center font-semibold text-green-900">{qtyInCart}</span>
                                                            <button
                                                                type="button"
                                                                disabled={!isOrderEditable || isOutOfStock}
                                                                onClick={() => handleSelectMenuItem(menuItem)}
                                                                className="flex h-8 w-8 items-center justify-center rounded-full border border-green-400 bg-white text-green-700 hover:bg-green-100 disabled:cursor-not-allowed disabled:opacity-50"
                                                            >
                                                                +
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            disabled={!isOrderEditable || isOutOfStock}
                                                            onClick={() => handleSelectMenuItem(menuItem)}
                                                            className="rounded-full bg-green-600 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                                                        >
                                                            {isOutOfStock ? 'Épuisé' : 'Ajouter'}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {!visibleDishes.length && (
                                        <p className="col-span-2 text-sm text-green-500">Aucun plat ne correspond à cette recherche.</p>
                                    )}
                                </div>
                            </div>

                            <div className="rounded-3xl border border-green-400 bg-white p-4">
                                <h4 className="mb-3 text-base font-semibold text-green-900">Résumé de la commande</h4>
                                {orderItems.length ? (
                                    <div className="space-y-3">
                                        {orderItems.map((item) => (
                                            <div
                                                key={item.menuItemId}
                                                className="rounded-3xl border border-green-400 bg-green-50 px-4 py-3"
                                            >
                                                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                                                    <div>
                                                        <p className="font-semibold text-green-900">{item.name}</p>
                                                        {item.category && (
                                                            <p className="text-xs uppercase tracking-[0.25em] text-green-600">
                                                                {item.category}
                                                            </p>
                                                        )}
                                                        <p className="mt-1 text-sm text-green-500">Ar {(item.menuItem?.price ?? 0).toFixed(2)}</p>
                                                        {item.notes && (
                                                            <p className="mt-1 text-sm text-green-700">Remarque: {item.notes}</p>
                                                        )}
                                                    </div>
                                                    <button
                                                        type="button"
                                                        disabled={!isOrderEditable}
                                                        className={`rounded-full border px-3 py-1 text-xs transition ${
                                                            isOrderEditable
                                                                ? 'border-green-300 text-green-700 hover:bg-green-200'
                                                                : 'border-slate-200 text-slate-400 cursor-not-allowed bg-slate-100'
                                                        }`}
                                                        onClick={() => handleRemoveItem(item.menuItemId)}
                                                    >
                                                        Retirer
                                                    </button>
                                                </div>
                                                <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                                    <label className="text-sm text-green-600" htmlFor={`qty-${item.menuItemId}`}>
                                                        Quantité
                                                    </label>
                                                    <input
                                                        id={`qty-${item.menuItemId}`}
                                                        type="number"
                                                        min="1"
                                                        value={item.quantity}
                                                        disabled={!isOrderEditable}
                                                        onChange={(event) => handleQuantityChange(item.menuItemId, event.target.value)}
                                                        className={`w-full max-w-[120px] rounded-2xl border px-3 py-2 text-sm focus:outline-none ${
                                                            isOrderEditable
                                                                ? 'border-green-300 bg-green-50 text-green-900 focus:border-green-500'
                                                                : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                                        }`}
                                                    />
                                                </div>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between text-sm text-green-600">
                                            <span>Total</span>
                                            <span className="font-semibold text-green-900">Ar {orderTotal.toFixed(2)}</span>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-green-500">Aucun plat ajouté. Choisissez une catégorie puis un plat à gauche.</p>
                                )}

                                {(order.orderedAt || order.readyAt || order.paidAt || rawOrder?.paidAt) && (
                                    <div className="mt-4 rounded-3xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                                        {(order.orderedAt || rawOrder?.orderedAt) && (
                                            <p>Commande envoyée : <span className="font-semibold text-green-900">{formatDateTime(order.orderedAt || rawOrder?.orderedAt)}</span></p>
                                        )}
                                        {(order.readyAt || rawOrder?.readyAt) && (
                                            <p>Commande prête : <span className="font-semibold text-green-900">{formatDateTime(order.readyAt || rawOrder?.readyAt)}</span></p>
                                        )}
                                        {(order.paidAt || rawOrder?.paidAt) && (
                                            <p>Commande payée : <span className="font-semibold text-green-900">{formatDateTime(order.paidAt || rawOrder?.paidAt)}</span></p>
                                        )}
                                    </div>
                                )}

                                <label className="mt-4 block text-sm text-green-600">Notes / remarques</label>
                                <textarea
                                    value={order.notes}
                                    disabled={!isOrderEditable}
                                    onChange={(event) => handleNoteChange(event.target.value)}
                                    className={`mt-2 h-28 w-full rounded-3xl border px-4 py-3 text-sm focus:outline-none ${
                                        isOrderEditable
                                            ? 'border-green-400 bg-green-50 text-green-900 focus:border-green-500'
                                            : 'border-slate-200 bg-slate-100 text-slate-400 cursor-not-allowed'
                                    }`}
                                    placeholder="Préférences, cuisson, allergènes..."
                                />

                                <div className="mt-4 flex flex-col gap-3">
                                    {(!selectedTable.order?.status || selectedTable.order?.status === 'draft') && (
                                        <button
                                            disabled={!orderItems.length || selectedTable.status === 'locked' || !selectedTable.id}
                                            onClick={() => selectedTable.id && onSendOrder(selectedTable.id)}
                                            className="rounded-3xl bg-green-600 px-4 py-3 font-semibold text-slate-950 transition hover:bg-green-500 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            Passer commande
                                        </button>
                                    )}

                                    {selectedTable.order && (
                                        <button
                                            onClick={() => onOpenPrepareModal && onOpenPrepareModal(selectedTable.order.id)}
                                            className="mt-2 rounded-3xl border border-amber-500 text-amber-700 px-4 py-3 font-semibold hover:bg-amber-50"
                                        >
                                            Préparer (choisir lots)
                                        </button>
                                    )}

                                    {isOrderPayable ? (
                                        <div className="rounded-3xl border border-green-200 bg-green-50 p-4">
                                            <h4 className="mb-3 text-base font-semibold text-green-900">Encaissement</h4>
                                            <p className="text-sm text-green-700">
                                                Montant dû : <span className="font-semibold text-green-900">Ar {paymentDue.toFixed(2)}</span>
                                            </p>

                                            <div className="mt-4 space-y-4">
                                                <div>
                                                    <label className="text-sm text-green-600">Mode de paiement</label>
                                                    <select
                                                        value={paymentMethod}
                                                        onChange={(e) => {
                                                            setPaymentMethod(e.target.value);
                                                            setPaymentReference('');
                                                            setAmountReceived('');
                                                        }}
                                                        className="mt-2 w-full rounded-3xl border border-green-400 bg-white px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                    >
                                                        <option value="cash">Espèces</option>
                                                        <option value="mobile">Mobile money</option>
                                                        <option value="card">Carte</option>
                                                        <option value="other">Autre</option>
                                                    </select>
                                                </div>

                                                {paymentMethod === 'cash' ? (
                                                    <div className="space-y-3">
                                                        <div>
                                                            <label className="text-sm text-green-600">Montant reçu</label>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                step="0.01"
                                                                value={amountReceived}
                                                                onChange={(e) => setAmountReceived(e.target.value)}
                                                                className="mt-2 w-full rounded-3xl border border-green-400 bg-white px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                            />
                                                        </div>
                                                        <p className="text-sm text-green-700">
                                                            Rendre : <span className="font-semibold text-green-900">Ar {cashChange.toFixed(2)}</span>
                                                        </p>
                                                    </div>
                                                ) : (
                                                    <div>
                                                        <label className="text-sm text-green-600">Référence de paiement</label>
                                                        <input
                                                            value={paymentReference}
                                                            onChange={(e) => setPaymentReference(e.target.value)}
                                                            className="mt-2 w-full rounded-3xl border border-green-400 bg-white px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                                            placeholder="Ex. numéro de transaction"
                                                        />
                                                    </div>
                                                )}
                                                {paymentError && (
                                                    <p className="text-sm text-red-600">{paymentError}</p>
                                                )}

                                                <button
                                                    onClick={async () => {
                                                        setPaymentError('');
                                                        if (paymentMethod === 'cash' && Number(amountReceived) < paymentDue) {
                                                            setPaymentError('Le montant reçu doit couvrir le montant dû.');
                                                            return;
                                                        }
                                                        if (['mobile', 'card', 'other'].includes(paymentMethod) && !paymentReference.trim()) {
                                                            setPaymentError('Veuillez saisir la référence de paiement.');
                                                            return;
                                                        }

                                                        try {
                                                            await onPayOrder(selectedTable.id, {
                                                                paymentMethod,
                                                                paymentReference: paymentReference.trim(),
                                                                amountReceived: paymentMethod === 'cash' ? Number(amountReceived) : null,
                                                                changeDue: paymentMethod === 'cash' ? cashChange : null,
                                                                paidAt: new Date().toISOString(),
                                                            });

                                                            printBrowserReceipt();
                                                            setAmountReceived('');
                                                            setPaymentReference('');
                                                        } catch (error) {
                                                            console.error('Erreur lors du paiement/impression:', error);
                                                            setPaymentError(error?.message || 'Une erreur est survenue.');
                                                        }
                                                    }}
                                                    className="w-full rounded-3xl bg-green-600 px-4 py-3 font-semibold text-slate-950 transition hover:bg-green-500"
                                                >
                                                    Enregistrer le paiement
                                                </button>
                                            </div>
                                        </div>
                                    ) : rawOrder && rawOrder.status !== 'completed' ? (
                                        <div className="rounded-3xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                                            <p>
                                                Paiement disponible après passage de la commande.
                                                Statut actuel : <span className="font-semibold text-green-900">{rawOrder.status || '—'}</span>
                                            </p>
                                        </div>
                                    ) : rawOrder && rawOrder.status === 'completed' ? (
                                        <div className="rounded-3xl border border-green-200 bg-green-50 p-4 text-sm text-green-700">
                                            <p>
                                                Dernière commande déjà payée{rawOrder.paidAt ? ` le ${formatDateTime(rawOrder.paidAt)}` : ''}.
                                                Ajoutez des plats ci-dessus pour démarrer une nouvelle commande.
                                            </p>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}